import { createResolvePivotKey, ResolvePivotKey } from '../shared/resolve-pivot-key';
import { NormalizedUnitValue } from '../shared/types';
import { createFpFactorOps, toNumber } from '../shared/utils';

/**
 * How one unit relates to the family's shared pivot (e.g. Celsius, seconds, Hz).
 * Cross conversions never need a pairwise formula: F→K is `kelvin.fromPivot(fahrenheit.toPivot(v))`.
 */
export type PivotMapping = {
  /** Convert a value in this unit into the family's pivot unit. */
  toPivot: (value: number) => number;
  /** Convert a value in the family's pivot unit back into this unit. */
  fromPivot: (value: number) => number;
};

/**
 * A plain number is linear sugar for `{ toPivot: v => v*n, fromPivot: v => v/n }`,
 * where `n` is "how many pivot units equal 1 of this unit" (e.g. `min: 60` when
 * the pivot is seconds). Use the explicit `{ toPivot, fromPivot }` form for affine
 * conversions (anything with a +/- offset, like temperature).
 */
export type PivotMappingInput = number | PivotMapping;

/** Identity pivot mapping for units that are already in pivot form. */
export const IDENTITY_PIVOT_MAPPING: PivotMapping = {
  toPivot: (value) => value,
  fromPivot: (value) => value,
};

const resolveMapping = (input: PivotMappingInput): PivotMapping => {
  if (typeof input !== 'number') {
    return input;
  }
  const ops = createFpFactorOps(input);
  return { toPivot: ops.multiplyBy, fromPivot: ops.divideBy };
};

/**
 * Builds a family transformer that converts any mapped unit to any other mapped
 * unit through a shared pivot. Pivot keys must match catalog asciiSymbol values
 * (lowercased); raw aliases resolve via canonicalAsciiByKey before lookup.
 */
export const createPivotTransformer = (
  unitToPivot: Record<string, PivotMappingInput>,
  resolvePivotKey?: ResolvePivotKey
) => {
  const resolvedMap: Record<string, PivotMapping> = Object.create(null);
  for (const [key, input] of Object.entries(unitToPivot)) {
    resolvedMap[key.toLowerCase()] = resolveMapping(input);
  }

  const resolveKey = resolvePivotKey ?? createResolvePivotKey(resolvedMap);

  return (unitValue: NormalizedUnitValue, newUnits?: string): NormalizedUnitValue => {
    const value = toNumber(unitValue.value);
    if (value == null || !newUnits) return unitValue;

    const sourceKey = resolveKey(unitValue.units);
    const targetKey = resolveKey(newUnits);
    if (!sourceKey || !targetKey) return unitValue;

    const source = resolvedMap[sourceKey];
    const target = resolvedMap[targetKey];

    return { value: target.fromPivot(source.toPivot(value)), units: newUnits };
  };
};
